/**
 * Teacher Tools Service for handling all teacher tool-related API calls
 */

import { defaultErrorHandler as commonErrorHandler, createHeaders as createCommonHeaders, getApiUrls } from './serviceConfig';

/**
 * Creates a configured teacher tools service
 * @param {Object} config - Configuration options
 * @param {string} config.baseUrl - Base URL for the API
 * @param {string} config.environment - Environment ('development' or 'production')
 * @param {Object} config.headers - Additional headers to include in all requests
 * @param {Function} config.onError - Global error handler
 * @param {Function} config.getAuthToken - Function to get the latest auth token (preferred)
 * @param {string} config.authToken - Static authentication token for API requests (not recommended)
 * @returns {Object} The teacher tools service API
 */
export function createTeacherToolsService(config = {}) {
  const DEFAULT_HEADERS = config.headers || {};
  const defaultErrorHandler = config.onError || ((error) => commonErrorHandler('Teacher Tools service', error));
  // Use getAuthToken function if provided, or use static token as fallback
  const getAuthToken = config.getAuthToken || (() => config.authToken);

  /**
   * Helper to create request headers
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Object} Headers object
   */
  const createHeaders = (additionalHeaders = {}) => {
    // Only pass custom auth getter if one was provided to the service
    const customAuthGetter = config.getAuthToken ? getAuthToken : null;
    return createCommonHeaders(
      {
        ...DEFAULT_HEADERS,
        ...additionalHeaders,
      },
      customAuthGetter
    );
  };

  /**
   * Helper to handle API errors
   * @param {Response} response - Fetch Response object
   * @param {string} errorPrefix - Prefix for error message
   * @returns {Promise} JSON response or throws error
   */
  const handleResponse = async (response, errorPrefix = 'API error') => {
    if (!response.ok) {
      let errorMessage = `${errorPrefix}: ${response.status}`;

      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = `${errorPrefix}: ${errorData.message}`;
        }
      } catch {
        // If we can't parse the error JSON, just use the status code
      }

      const error = new Error(errorMessage);
      error.statusCode = response.status;
      throw error;
    }

    return response.json();
  };

  return {
    /**
     * Create a Real World Connection conversation
     * @param {Object} options - Configuration for the real world connection
     * @param {string} options.educationYear - The education year (e.g., "1º ESO")
     * @param {string} options.educationLevel - The education level (e.g., "Secundaria")
     * @param {string} options.textToAdapt - The text content to adapt
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createRealWorldConnection: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
        };

        // Include additional notes if provided
        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.realWorldConnection(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating real world connection');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Get contents by IDs (unified endpoint for lessons and units)
     * @param {string[]} contentIds - Array of content IDs to retrieve
     * @returns {Promise<Object>} Response with contents
     * @throws {Error} If request fails
     */
    getContentsContent: async (contentIds) => {
      try {
        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.getContentsContent(), {
          method: 'POST',
          headers: createHeaders(),
          body: JSON.stringify({ contentIds }),
        });

        if (!response.ok) {
          const error = new Error(`Failed to fetch contents: ${response.status}`);
          error.statusCode = response.status;
          throw error;
        }

        return await response.json();
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Get teacher tools history with pagination
     * @param {Object} options - Pagination and filter options
     * @param {number} options.limit - Number of items per page (default: 20)
     * @param {number} options.offset - Offset for pagination (default: 0)
     * @returns {Promise<Object>} Response with items, total, limit, and offset
     */
    getHistory: async (options = {}) => {
      try {
        const { limit = 20, offset = 0 } = options;
        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.history({ limit, offset }), {
          method: 'GET',
          headers: createHeaders(),
        });

        const data = await handleResponse(response, 'Error fetching teacher tools history');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Create a Text Level Adapter conversation
     * @param {Object} options - Configuration for the text level adapter
     * @param {string} options.educationYear - The education year (e.g., "1º ESO")
     * @param {string} options.educationLevel - The education level (e.g., "Secundaria")
     * @param {string} options.textToAdapt - The text content to adapt
     * @param {Array<string>} options.specialNeeds - Optional array of special needs
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createTextLevelAdapter: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
        };

        // Include special needs if provided
        if (options.specialNeeds) {
          requestBody.specialNeeds = options.specialNeeds;
        }

        // Include additional notes if provided
        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.textLevelAdapter(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating text level adapter');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Create a Vocabulary Creator conversation
     * @param {Object} options - Configuration for the vocabulary creator
     * @param {string} options.educationYear - The education year (e.g., "1º ESO")
     * @param {string} options.educationLevel - The education level (e.g., "Secundaria")
     * @param {string} options.textToAdapt - The text content to extract vocabulary from
     * @param {number} options.numberOfWordsToExtract - Number of vocabulary words to extract (1-50)
     * @param {Array<string>} options.specialNeeds - Optional array of special needs
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createVocabularyCreator: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
          numberOfWordsToExtract: options.numberOfWordsToExtract || 10,
        };

        // Include special needs if provided
        if (options.specialNeeds) {
          requestBody.specialNeeds = options.specialNeeds;
        }

        // Include additional notes if provided
        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.vocabularyCreator(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating vocabulary creator');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Create a Lesson Plan Generator conversation
     * @param {Object} options - Configuration for the lesson plan generator
     * @param {string} options.educationYear - The education year (e.g., "1º Primaria")
     * @param {string} options.educationLevel - The education level (e.g., "Primaria")
     * @param {string} options.textToAdapt - The lesson content/topic to generate plan for
     * @param {string} options.duration - Lesson duration (e.g., "45 minutos", "1 hora")
     * @param {string[]} options.evaluationCriteria - Optional array of evaluation criteria
     * @param {string} options.specialNeeds - Optional special needs (e.g., "Dyslexia, ADHD")
     * @param {string} options.resources - Optional available resources
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createLessonPlanGenerator: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
          duration: options.duration,
        };

        // Include optional fields if provided
        if (options.evaluationCriteria && Array.isArray(options.evaluationCriteria)) {
          requestBody.evaluationCriteria = options.evaluationCriteria;
        }

        if (options.specialNeeds) {
          requestBody.specialNeeds = options.specialNeeds;
        }

        if (options.resources) {
          requestBody.resources = options.resources;
        }

        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.lessonPlanGenerator(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating lesson plan generator');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Create a Text Translator conversation
     * @param {Object} options - Configuration for the text translator
     * @param {string} options.educationYear - The education year (e.g., "1º ESO")
     * @param {string} options.educationLevel - The education level (e.g., "Secundaria")
     * @param {string} options.textToAdapt - The text content to translate
     * @param {string} options.destinationLanguage - ISO language code (e.g., "ca-valencia", "en", "fr")
     * @param {string} options.specialNeeds - Optional special needs (e.g., "Dyslexia, ADHD")
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createTextTranslator: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
          destinationLanguage: options.destinationLanguage,
        };

        // Include special needs if provided
        if (options.specialNeeds) {
          requestBody.specialNeeds = options.specialNeeds;
        }

        // Include additional notes if provided
        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.textTranslator(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating text translator');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Create a Classroom Dynamics conversation
     * @param {Object} options - Configuration for the classroom dynamics
     * @param {string} options.educationYear - The education year (e.g., "1º ESO")
     * @param {string} options.educationLevel - The education level (e.g., "Secundaria")
     * @param {string} options.textToAdapt - The text content to adapt
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createClassroomDynamics: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
        };

        // Include additional notes if provided
        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.classroomDynamics(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating classroom dynamics');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Create a Rubric Generator conversation
     * @param {Object} options - Configuration for the rubric generator
     * @param {string} options.educationYear - The education year (e.g., "1º ESO")
     * @param {string} options.educationLevel - The education level (e.g., "Secundaria")
     * @param {string} options.textToAdapt - The task/topic to create a rubric for
     * @param {number} options.numberOfCriteria - Number of evaluation criteria (1-8)
     * @param {string} options.scale - Grading scale type ("1-4", "1-6", or "1-10")
     * @param {string[]} options.evaluationCriteria - Optional array of evaluation criteria
     * @param {string} options.specialNeeds - Optional special needs (e.g., "Dyslexia, ADHD")
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createRubricGenerator: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
          numberOfCriteria: options.numberOfCriteria,
          scale: options.scale,
        };

        // Include optional fields if provided
        if (options.evaluationCriteria && Array.isArray(options.evaluationCriteria)) {
          requestBody.evaluationCriteria = options.evaluationCriteria;
        }

        if (options.specialNeeds) {
          requestBody.specialNeeds = options.specialNeeds;
        }

        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.rubricGenerator(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating rubric generator');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Create a Student Feedback conversation
     * @param {Object} options - Configuration for the student feedback
     * @param {string} options.educationYear - The education year (e.g., "1º ESO")
     * @param {string} options.educationLevel - The education level (e.g., "Secundaria")
     * @param {string} options.textToAdapt - The student's submitted work
     * @param {string} options.expectedResponse - Optional model answer or expected response
     * @param {Array<string>} options.evaluationCriteria - Optional array of evaluation criteria
     * @param {string} options.specialNeeds - Optional special needs (e.g., "Dyslexia, ADHD")
     * @param {string} options.additionalNotes - Optional additional notes or requirements
     * @returns {Promise<Object>} Response with conversationId
     */
    createStudentFeedback: async (options = {}) => {
      try {
        const requestBody = {
          educationYear: options.educationYear,
          educationLevel: options.educationLevel,
          textToAdapt: options.textToAdapt,
        };

        // Include expected response if provided
        if (options.expectedResponse) {
          requestBody.expectedResponse = options.expectedResponse;
        }

        // Include evaluation criteria if provided
        if (options.evaluationCriteria && options.evaluationCriteria.length > 0) {
          requestBody.evaluationCriteria = options.evaluationCriteria;
        }

        // Include special needs if provided
        if (options.specialNeeds) {
          requestBody.specialNeeds = options.specialNeeds;
        }

        // Include additional notes if provided
        if (options.additionalNotes) {
          requestBody.additionalNotes = options.additionalNotes;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.studentFeedback(), {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        const data = await handleResponse(response, 'Error creating student feedback');

        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Get available activity types for AI quiz generation
     * @param {boolean} enabledOnly - Whether to fetch only enabled activity types
     * @returns {Promise<Object>} Response with activity types array
     */
    getActivityTypes: async (enabledOnly = true) => {
      try {
        const urls = getApiUrls();
        const response = await fetch(urls.teacherTools.activityTypes({ enabledOnly }), {
          method: 'GET',
          headers: createHeaders(),
        });
        return await handleResponse(response, 'Error fetching activity types');
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },
  };
}

// Default export with default configuration
export default createTeacherToolsService();
